import { useState } from 'react';
import { Box, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AuthDialog } from '../../features/auth';
import {
  notificationSelectors,
  useNotificationStore,
} from '../../features/notifications';
import { useUserStore, userSelectors } from '../../entities/user';
import { useI18n } from '../../shared/i18n';
import { AppFooter } from './AppFooter';
import { AppHero } from './AppHero';
import { AppLoadingState } from './AppLoadingState';
import { AppNotifications } from './AppNotifications';
import { AppRoutes } from './AppRoutes';
import { AppTopBar } from './AppTopBar';
import { useAppShellState } from './useAppShellState';

const Shell = styled(Box)({
  minHeight: '100vh',
  position: 'relative',
  overflow: 'hidden',
});

const Frame = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(8),
}));

export function App() {
  const { t } = useI18n();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const bootstrap = useUserStore(userSelectors.bootstrap);
  const logout = useUserStore(userSelectors.logout);
  const hydrated = useUserStore(userSelectors.hydrated);
  const user = useUserStore(userSelectors.user);
  const loading = useUserStore(userSelectors.bootstrapLoading);
  const notifications = useNotificationStore(
    notificationSelectors.notifications
  );
  const dismissNotification = useNotificationStore(
    notificationSelectors.dismissNotification
  );
  const notify = useNotificationStore(notificationSelectors.notify);

  const { handleInstall, installDisabled, updatedAt } = useAppShellState({
    bootstrap,
    notifications,
    dismissNotification,
    notify,
  });

  const loadingState = <AppLoadingState label={t('app.connecting')} />;

  const openAuthDialog = () => {
    setAuthDialogOpen(true);
  };

  const closeAuthDialog = () => {
    setAuthDialogOpen(false);
  };

  return (
    <Shell>
      <AppTopBar
        installDisabled={installDisabled}
        onInstall={handleInstall}
        user={user}
        onLogout={logout}
        onAuthorize={openAuthDialog}
      />
      <Frame maxWidth="lg">
        <AppHero />

        {!hydrated || loading ? loadingState : null}

        {hydrated && !loading ? (
          <AppRoutes loadingFallback={loadingState} />
        ) : null}

        <AppFooter
          updatedAt={updatedAt}
          installDisabled={installDisabled}
          onInstall={handleInstall}
          user={user}
        />
      </Frame>
      <AppNotifications
        notifications={notifications}
        onDismiss={dismissNotification}
      />
      <AuthDialog open={authDialogOpen} onClose={closeAuthDialog} />
    </Shell>
  );
}
